import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/hooks/use-auth";
import { useToast } from "@/hooks/use-toast";
import { ArrowLeft, Plus, FolderOpen, ExternalLink, Trash2, X, FileText, Link2 } from "lucide-react";
import type { Resource } from "@shared/schema";

const RESOURCE_CATEGORIES = ["general", "templates", "guides", "tools", "frameworks", "reading"];

export default function CommunityResources() {
  const { isAuthenticated, isAdmin, isLoading } = useAuth();
  const [, navigate] = useLocation();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [showNew, setShowNew] = useState(false);
  const [filterCategory, setFilterCategory] = useState("");
  const [form, setForm] = useState({ title: "", description: "", url: "", category: "general" });

  const { data: resources } = useQuery<Resource[]>({
    queryKey: ["/api/resources", filterCategory],
    queryFn: async () => {
      const url = filterCategory ? `/api/resources?category=${filterCategory}` : "/api/resources";
      const res = await fetch(url, { credentials: "include" });
      if (res.status === 401) { navigate("/login"); return []; }
      if (!res.ok) throw new Error("Failed to fetch");
      return res.json();
    },
    enabled: isAuthenticated,
  });

  const createResource = useMutation({
    mutationFn: async (data: any) => {
      const res = await fetch("/api/resources", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
        credentials: "include",
      });
      if (!res.ok) throw new Error("Failed to create resource");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/resources"] });
      setShowNew(false);
      setForm({ title: "", description: "", url: "", category: "general" });
      toast({ title: "Resource added" });
    },
  });

  const deleteResource = useMutation({
    mutationFn: async (id: number) => {
      const res = await fetch(`/api/resources/${id}`, { method: "DELETE", credentials: "include" });
      if (!res.ok) throw new Error("Failed to delete");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/resources"] });
      toast({ title: "Resource deleted" });
    },
  });

  if (isLoading) return <><Navbar /><main id="main-content" className="pt-20 min-h-screen flex items-center justify-center"><div className="text-muted-foreground" role="status" aria-live="polite">Loading...</div></main></>;
  if (!isAuthenticated) { navigate("/login"); return null; }

  return (
    <>
      <Navbar />
      <main id="main-content" className="pt-20 min-h-screen">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-12">
          <Button variant="ghost" className="mb-6 -ml-4 text-muted-foreground" onClick={() => navigate("/community")}>
            <ArrowLeft size={16} className="mr-2" aria-hidden="true" /> Community Hub
          </Button>

          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
            <div>
              <h1 className="text-3xl font-display font-bold mb-2">Resources</h1>
              <p className="text-muted-foreground">Templates, guides, tools, and frameworks to help you grow.</p>
            </div>
            {isAdmin && (
              <Button onClick={() => setShowNew(true)} className="self-start sm:self-auto shrink-0" data-testid="button-new-resource">
                <Plus size={18} className="mr-2" aria-hidden="true" /> Add Resource
              </Button>
            )}
          </div>

          <div className="flex gap-2 flex-wrap mb-8" role="group" aria-label="Filter by category">
            <Badge
              className={`cursor-pointer ${!filterCategory ? "bg-primary text-primary-foreground" : "bg-white/5 text-muted-foreground hover:text-foreground"}`}
              onClick={() => setFilterCategory("")}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setFilterCategory(""); } }}
              aria-pressed={!filterCategory}
            >
              All
            </Badge>
            {RESOURCE_CATEGORIES.map((cat) => (
              <Badge
                key={cat}
                className={`cursor-pointer capitalize ${filterCategory === cat ? "bg-primary text-primary-foreground" : "bg-white/5 text-muted-foreground hover:text-foreground"}`}
                onClick={() => setFilterCategory(cat)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setFilterCategory(cat); } }}
                aria-pressed={filterCategory === cat}
              >
                {cat}
              </Badge>
            ))}
          </div>

          {showNew && isAdmin && (
            <Card className="border-white/10 p-6 mb-8">
              <div className="flex justify-between items-center mb-4">
                <h2 className="font-semibold text-lg">Add New Resource</h2>
                <button onClick={() => setShowNew(false)} aria-label="Close new resource form" className="p-3 -m-2 rounded-md hover:bg-white/5 transition-colors"><X size={18} className="text-muted-foreground" aria-hidden="true" /></button>
              </div>
              <form className="space-y-4" aria-label="Add new resource" onSubmit={(e) => { e.preventDefault(); createResource.mutate(form); }}>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="resource-title">Title</Label>
                    <Input id="resource-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Resource title" className="bg-background border-white/10" required data-testid="input-resource-title" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="resource-category">Category</Label>
                    <select
                      id="resource-category"
                      value={form.category}
                      onChange={(e) => setForm({ ...form, category: e.target.value })}
                      className="w-full bg-background border border-white/10 rounded-md px-3 py-2.5 text-sm min-h-[44px]"
                      data-testid="select-resource-category"
                    >
                      {RESOURCE_CATEGORIES.map((cat) => (
                        <option key={cat} value={cat}>{cat}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="resource-url">URL</Label>
                  <Input id="resource-url" value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} placeholder="https://..." className="bg-background border-white/10" required data-testid="input-resource-url" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="resource-description">Description</Label>
                  <Textarea id="resource-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Brief description..." rows={3} className="bg-background border-white/10" data-testid="input-resource-description" />
                </div>
                <Button type="submit" disabled={createResource.isPending} aria-busy={createResource.isPending} data-testid="button-create-resource">
                  {createResource.isPending ? "Adding..." : "Add Resource"}
                </Button>
              </form>
            </Card>
          )}

          {resources && resources.length > 0 ? (
            <div className="grid md:grid-cols-2 gap-4">
              {resources.map((resource) => (
                <Card key={resource.id} className="border-white/5 p-5 hover:border-primary/30 transition-colors group" data-testid={`card-resource-${resource.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2">
                        <FileText size={16} className="text-amber-400 shrink-0" aria-hidden="true" />
                        <Badge variant="secondary" className="bg-white/5 text-xs capitalize">{resource.category}</Badge>
                      </div>
                      <h2 className="font-semibold text-base mb-1 truncate">{resource.title}</h2>
                      {resource.description && <p className="text-sm text-muted-foreground mb-3 line-clamp-2">{resource.description}</p>}
                      <a
                        href={resource.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary text-sm flex items-center gap-1 hover:underline"
                      >
                        <Link2 size={14} aria-hidden="true" /> Open Resource <ExternalLink size={12} aria-hidden="true" /> <span className="sr-only">(opens in new tab)</span>
                      </a>
                    </div>
                    {isAdmin && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-400 shrink-0"
                        aria-label={`Delete resource: ${resource.title}`}
                        onClick={() => { if (confirm("Delete this resource?")) deleteResource.mutate(resource.id); }}
                      >
                        <Trash2 size={14} aria-hidden="true" />
                      </Button>
                    )}
                  </div>
                </Card>
              ))}
            </div>
          ) : (
            <div className="text-center py-16 bg-card border border-white/5 rounded-2xl">
              <FolderOpen size={32} className="mx-auto mb-4 text-muted-foreground" aria-hidden="true" />
              <h2 className="font-semibold mb-2">No resources yet</h2>
              <p className="text-muted-foreground text-sm">Resources will be added here by the admin.</p>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}
